'use client';

import { memo } from 'react';
import { MessagePrimitive, useMessage } from '@assistant-ui/react';
import { Bot, User, Download, ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MomentCard } from '../moments/MomentCard';
import { useMomento } from '@/hooks/useMomento';
import { cn } from '@/lib/utils';
import { stripSuggestionsFromContent } from '@/lib/utils/parse-agent-suggestions';

interface MessageContentProps {
  content: string;
  messageId: string;
  onMomentSelect: (moment: any) => void;
}

/**
 * Normalize MUX URLs that may be split across multiple lines by the LLM
 * Collapses: https://stream.mux.com/abc.m3u8\n?start=1&end=2 → single line
 */
function normalizeMuxUrls(text: string): string {
  // Pattern: MUX URL base followed by newlines/whitespace then query params
  // Handles cases where LLM formats URL across multiple lines for "readability"
  const multilineMuxPattern = /(https:\/\/stream\.mux\.com\/[a-zA-Z0-9]+[\/\.]m3u8)\s*\n\s*(\?[^`\n]*(?:\n\s*&[^`\n]*)*)/g;

  return text.replace(multilineMuxPattern, (match, base, params) => {
    // Collapse the URL: remove newlines and extra whitespace from params
    const cleanParams = params.replace(/\n\s*/g, '');
    return base + cleanParams;
  });
}

/**
 * Custom link component for ReactMarkdown that handles download links
 * Forces programmatic download for storage URLs instead of opening in new tab
 */
const createLinkComponent = () => {
  return ({ node, href, children, ...props }: any) => {
    const isDownload = href?.includes('downloads/') ||
                      href?.includes('storage.') ||
                      href?.includes('supabase.co/storage') ||
                      href?.includes('.mp4') ||
                      href?.includes('.mov') ||
                      href?.includes('.srt') ||
                      href?.includes('.vtt') ||
                      href?.includes('.txt') ||
                      href?.includes('.json') ||
                      href?.includes('output.');

    // Handle download links with programmatic download to avoid navigation
    if (isDownload) {
      return (
        <button
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();

            try {
              const response = await fetch(href);
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = href.split('/').pop()?.split('?')[0] || 'download';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            } catch (error) {
              console.error('Download failed:', error);
              window.open(href, '_blank');
            }
          }}
          className={cn(
            "inline-flex items-center gap-1.5 transition-colors cursor-pointer",
            "px-3 py-1.5 rounded-lg bg-accent-blue-500 hover:bg-accent-blue-600 text-white font-medium no-underline"
          )}
        >
          <Download className="h-3.5 w-3.5" />
          <span>{children}</span>
        </button>
      );
    }

    // Regular external links open in new tab
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "inline-flex items-center gap-1.5 transition-colors",
          "text-accent-blue-600 hover:text-accent-blue-700 underline"
        )}
        {...props}
      >
        <span>{children}</span>
        <ExternalLink className="h-3 w-3 opacity-70" />
      </a>
    );
  };
};

// Shared markdown components configuration
const markdownComponents = {
  a: createLinkComponent(),
};

/**
 * Custom message content renderer that parses MUX URLs and renders MomentCards inline
 */
const MessageContent = memo(function MessageContent({ content, messageId, onMomentSelect }: MessageContentProps) {
  // Normalize multiline MUX URLs before regex matching (defensive fix)
  const normalizedContent = normalizeMuxUrls(content);

  // MUX URL regex: Support both /m3u8 (path) and .m3u8 (extension) formats
  // Also handles URLs inside markdown link syntax: [text](url)
  // PlaybackId can contain alphanumeric, dashes, and underscores
  const muxUrlRegex = /(?:\[[^\]]*\]\()?(https:\/\/stream\.mux\.com\/([a-zA-Z0-9_-]+)[\/\.]m3u8\?([^)\s]+))\)?/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let partIndex = 0;

  while ((match = muxUrlRegex.exec(normalizedContent)) !== null) {
    // Capture groups: [0]=fullMatch (with optional markdown), [1]=URL only, [2]=playbackId, [3]=queryString
    const [fullMatch, , playbackId, queryString] = match;
    const matchStart = match.index;

    const params = new URLSearchParams(queryString);
    const startTime = params.get('start');
    const endTime = params.get('end');
    const postId = params.get('postId');
    const momentType = params.get('momentType');
    const postTitle = params.get('postTitle');
    const title = params.get('title'); // Moment title (separate from postTitle)
    const similarity = params.get('similarity');
    const videoType = params.get('videoType') || 'moment';

    // Add text before moment
    if (matchStart > lastIndex) {
      parts.push(
        <div key={`${messageId}-text-${partIndex++}`} className="text-base prose dark:prose-invert max-w-none prose-p:leading-relaxed prose-headings:font-semibold">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {normalizedContent.slice(lastIndex, matchStart)}
          </ReactMarkdown>
        </div>
      );
    }

    // Add MomentCard with videoType for content type differentiation
    parts.push(
      <MomentCard
        key={`${messageId}-moment-${partIndex++}`}
        playbackId={playbackId}
        startTime={parseFloat(startTime || '0')}
        endTime={parseFloat(endTime || '0')}
        videoType={videoType as 'moment' | 'full' | 'memo' | 'preview'}
        postTitle={postTitle || undefined}
        onWatch={() => {
          const isFullVideo = videoType === 'full';
          const momentData = {
            playbackId,
            postId: postId || undefined,
            startTime: parseFloat(startTime || '0'),
            endTime: parseFloat(endTime || '0'),
            // For full videos, use postTitle as the display title
            title: isFullVideo ? (postTitle || title || 'Full Video') : (title || undefined),
            // Set momentType to 'full_video' for full videos
            momentType: isFullVideo ? 'full_video' : (momentType || undefined),
            postTitle: postTitle || undefined,
            similarity: similarity ? parseFloat(similarity) : undefined,
          };
          console.log('[MessageContent] Watch clicked, setting selected moment:', momentData);
          onMomentSelect(momentData);
        }}
      />
    );

    lastIndex = matchStart + fullMatch.length;
  }

  // Add remaining text (MUST use markdownComponents for consistent download handling)
  if (lastIndex < normalizedContent.length) {
    parts.push(
      <div key={`${messageId}-text-${partIndex++}`} className="text-base prose dark:prose-invert max-w-none prose-p:leading-relaxed prose-headings:font-semibold">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={markdownComponents}
        >
          {normalizedContent.slice(lastIndex)}
        </ReactMarkdown>
      </div>
    );
  }

  return <div className="space-y-2">{parts}</div>;
}, (prevProps, nextProps) => {
  return prevProps.content === nextProps.content &&
         prevProps.messageId === nextProps.messageId;
});

/**
 * Custom User Message Component
 * Supports text content and image attachments (for GPT-5.2 vision)
 */
export function CustomUserMessage() {
  const { setSelectedMoment } = useMomento();
  const message = useMessage();

  // Extract text content - handle both string and array formats
  let textContent = '';
  const imageUrls: string[] = [];

  // Process message content (could be string, text part, or array with images)
  if (message.content) {
    for (const part of message.content) {
      if (part.type === 'text') {
        textContent += (part as { type: 'text'; text: string }).text || '';
      } else if (part.type === 'image') {
        // assistant-ui uses 'image' type with 'image' property containing URL
        const imagePart = part as { type: 'image'; image: string };
        if (imagePart.image) {
          imageUrls.push(imagePart.image);
        }
      }
    }
  }

  // Strip context prefix from display (added server-side for agent context)
  // Context format: [Currently Viewing: ...]\n\n or [Currently Editing Memo: ...]\n\n
  textContent = textContent
    .replace(/\[Currently Viewing:.*?\](\n\n)?/s, '')
    .replace(/\[Currently Editing Memo:.*?\](\n\n)?/s, '')
    .replace(/\[SYSTEM REMINDER:.*?\](\n\n)?/s, '');

  return (
    <MessagePrimitive.Root className="flex gap-3 justify-end">
      <div className="max-w-[80%] space-y-2">
        {/* Render image attachments */}
        {imageUrls.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-end">
            {imageUrls.map((url, index) => (
              <div key={index} className="relative">
                <img
                  src={url}
                  alt={`Uploaded image ${index + 1}`}
                  className="max-w-[200px] max-h-[200px] rounded-lg border object-contain bg-muted"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        )}

        {/* Render text content */}
        {textContent && (
          <div className="rounded-2xl px-4 py-3 bg-accent-blue-500 text-white">
            <MessageContent
              content={textContent}
              messageId={message.id}
              onMomentSelect={setSelectedMoment}
            />
          </div>
        )}
      </div>
      <div className="w-8 h-8 rounded-full bg-accent-blue-100 dark:bg-accent-blue-900/30 flex items-center justify-center flex-shrink-0">
        <User className="w-4 h-4 text-accent-blue-600 dark:text-accent-blue-400" />
      </div>
    </MessagePrimitive.Root>
  );
}

/**
 * Custom Assistant Message Component - SIMPLIFIED VERSION
 * Uses default assistant-ui rendering for Tool UIs
 */
/**
 * ToolGroup component for wrapping consecutive tool calls
 * Required by MessagePrimitive.Parts to render tool invocations
 */
const ToolGroup: React.FC<React.PropsWithChildren<{ startIndex: number; endIndex: number }>> =
  ({ children }) => {
    return <div className="space-y-2">{children}</div>;
  };

export function CustomAssistantMessage() {
  const { setSelectedMoment } = useMomento();
  const message = useMessage();

  // Find ALL text parts (not just first one!)
  const textParts = message.content.filter((p: any) => p.type === 'text');
  const rawContent = textParts.map((p: any) => p.text).join('\n\n');

  // Strip [SUGGESTED_PROMPTS]...[/SUGGESTED_PROMPTS] blocks from display
  const content = stripSuggestionsFromContent(rawContent);

  console.log('[CustomAssistantMessage] Rendering:', {
    id: message.id,
    status: message.status,
    contentLength: message.content?.length,
    contentTypes: message.content?.map((p: any) => p.type),
    textPartCount: textParts.length,
    hasTextContent: !!content,
    textPreview: content.substring(0, 50),
  });

  return (
    <MessagePrimitive.Root className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
        <Bot className="w-4 h-4 text-purple-600 dark:text-purple-400" />
      </div>
      <div className="max-w-[80%] flex flex-col gap-2">
        {/* Render text content directly from message.content (not via Parts) */}
        {content && (
          <div className="rounded-2xl px-4 py-3 bg-secondary text-secondary-foreground">
            <MessageContent
              content={content}
              messageId={message.id}
              onMomentSelect={setSelectedMoment}
            />
          </div>
        )}

        {/* ONLY use MessagePrimitive.Parts for tool calls */}
        <MessagePrimitive.If hasToolInvocations>
          <MessagePrimitive.Parts
            components={{
              ToolGroup,
              // Skip Text rendering in Parts (we already rendered it above)
              Text: () => null,
            }}
          />
        </MessagePrimitive.If>
      </div>
    </MessagePrimitive.Root>
  );
}
